<template>
	<v-menu
		v-model="menu"
		location="bottom left"
		origin="top left"
		transition="scale-transition"
	>
		<template #activator="{ props }">
			<v-btn v-bind="props" data-v-step="profileButton" data-tour="profileButton">
				<v-badge :model-value="typeof lia !== 'undefined'">
					<ProfileImage
						:class="{ 'mr-2': $vuetify.display.mdAndUp }"
						:user="whoami"
						:avatar-size="30"
					></ProfileImage>
					<template #badge>
						<v-icon icon="fas fa-eye" color="warning"></v-icon>
					</template>
				</v-badge>
				<div
					v-if="$vuetify.display.mdAndUp"
					:style="{
						'max-width': '10vw',
						'text-transform': 'none',
					}"
					class="text--overflow-hidden"
					:class="{ 'text-warning': typeof lia !== 'undefined' }"
				>
					{{
						personBuildFullname(whoami?.persons[0] ?? whoami?.team?.full_name)
					}}
				</div>
				<template #append>
					<v-icon :icon="`fa-solid fa-caret-down`"></v-icon>
				</template>
			</v-btn>
		</template>
		<v-card width="400" max-width="calc(100vw - 32px)">
			<v-list color="primary">
				<v-list-item
					:title="
						authenticated_user
							? personBuildFullname(authenticated_user, {
									isTeam: authenticated_user.team.is_team,
								})
							: personBuildFullname(
									whoami?.persons[0] ?? whoami?.team?.full_name
								)
					"
					:subtitle="
						authenticated_user ? authenticated_user?.email : whoami?.email
					"
				>
					<template #prepend>
						<ProfileImage
							:user="authenticated_user ?? whoami"
							:avatar-size="30"
						></ProfileImage>
					</template>
				</v-list-item>
				<v-list-item
					v-if="lia"
					base-color="warning"
					prepend-icon="far fa-eye"
					:title="translate('common.fromPerspectiveOf')"
					:subtitle="personBuildFullname(lia)"
					density="compact"
					lines="two"
				>
					<template #append>
						<v-btn
							icon="$close"
							variant="flat"
							class="lia-close"
							@click="liaRemove(router)"
						></v-btn>
					</template>
				</v-list-item>
				<v-divider></v-divider>
				<v-list-item
					v-for="(item, index) in items"
					:key="index"
					:title="item.title"
					:prepend-icon="item.icon"
					:disabled="item.disabled"
					:to="item.to"
					density="comfortable"
					:data-test="item.dataTest"
				>
				</v-list-item>
				<!-- AAR-1805: Zugang zur Hilfe auf xs. Dort zeigt die Toolbar den
				     Fragezeichen-Button nicht, weil sie bei 375px bereits voll ist.
				     canOpenHelp riegelt wie beim Toolbar-Button auf Admin/Team ab —
				     ohne das bliebe auf xs genau der Einstieg offen, den der
				     Toolbar-Button dort nicht anbietet. -->
				<v-list-item
					v-if="xs && canOpenHelp"
					prepend-icon="fa-solid fa-circle-question"
					title="Hilfe"
					density="comfortable"
					data-test="profile-menu__help-item"
					@click="openHelp"
				>
				</v-list-item>
				<LocaleMenu></LocaleMenu>
				<ThemeMenu></ThemeMenu>
				<!-- AAR-1870: nur auf Desktop relevant, dort greift die Inhaltsbreite -->
				<ContentAlignMenu v-if="mdAndUp"></ContentAlignMenu>
				<v-divider></v-divider>
				<v-list-item>
					<ButtonIcon
						:to="{ name: 'logout' }"
						color="error"
						icon="fas fa-sign-out-alt"
						text="Logout"
						block
						variant="tonal"
					/>
				</v-list-item>
			</v-list>
		</v-card>
	</v-menu>
</template>

<script setup lang="ts">
import ProfileImage from '@/components/templates/Modules/Users/ProfileImage.vue'
import { useLiaStore, useUserStore } from '@/stores/system'
import { useRouter } from 'vue-router'
import { usePersonFullname } from 'composables/personBuildFullname'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import LocaleMenu from '../templates/LocaleMenu.vue'
import ThemeMenu from '../templates/ThemeMenu.vue'
import ContentAlignMenu from '../templates/ContentAlignMenu.vue'
import { useTranslate } from 'composables/useTranslate.ts'
import { useHelpPortal } from '@/composables/useHelpPortal'
import { useDisplay } from 'vuetify'

const { translate } = useTranslate()
const { openHelp, canOpenHelp } = useHelpPortal()
// useDisplay() statt $vuetify im Template: letzteres ist in <script setup>
// nicht typisiert und wuerde einen weiteren vue-tsc-Fehler erzeugen.
const { xs, mdAndUp } = useDisplay()
const { whoami } = storeToRefs(useUserStore())
const { personBuildFullname } = usePersonFullname()
const router = useRouter()

const menu = ref(false)

const { authenticated_user, person: lia } = storeToRefs(useLiaStore())
const { liaRemove } = useUserStore()
const items = computed(() => [
	{
		title: translate('component.profile.name'),
		icon: translate('component.profile.icon'),
		to: { name: 'profile' },
		dataTest: 'profile-menu__profile-list-item',
	},
	{
		title: translate('component.profile.change_password'),
		icon: 'fal fa-key',
		disabled: typeof lia.value !== 'undefined',
		dataTest: 'profile-menu__change-password-item',
		to: {
			name: 'changePassword',
			query: {
				reset: false,
				routeNameOnClose: 'profile',
				email: whoami.value?.email,
			},
		},
	},
])
</script>

<style scoped>
/* AAR-950: Der X-Button (44px xs / 48px Desktop) sitzt sonst nicht auf der
   Flucht der Chevron-Icons (20px-Box, Mitte 10px vom rechten Rand). Um halbe
   Eigenbreite minus 10px nach rechts ruecken — 50% bezieht sich auf den
   Append-Container, der genau so breit ist wie der Button. Negative Margins
   gehen nicht: max-width:100% + auto-Grid-Spalte wuerden den Button stauchen. */
.lia-close {
	inset-inline-start: calc(50% - 10px);
}

/* AAR-950: In xs ist das 400px-Menue breiter als der Viewport. Sobald es
   geklemmt wird, wuerden Vuetifys Defaults (white-space:nowrap +
   text-overflow:ellipsis) die Beschriftung "Aus der Sicht von" und den
   Personennamen abschneiden. Umbrechen statt kuerzen, analog AAR-948 in
   Person.vue. */
@media (max-width: 599.98px) {
	:deep(.v-list-item-title),
	:deep(.v-list-item-subtitle) {
		white-space: normal;
		overflow: visible;
		text-overflow: clip;
	}
}
</style>
